<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'
import { ChevronDown, Copy, Pause, Pencil, Play, Trash2 } from 'lucide-vue-next'
import QrcodeVue from 'qrcode.vue'

import CopyLink from '@/components/CopyLink.vue'
import ContestsModal from './Components/ContestsModal.vue'
import ContestStatus from './Components/ContestStatus.vue'

import useSessionStorage from '@/composables/sessionStorage'
interface Store {
  id: number
  slug: string
  name: string
}

interface ShortLink {
  shortLink: string
  clicksCount: number
}

interface StoreWithShortLink {
  storeId: number
  name: string
  shortLink: ShortLink
}

interface ContestGameReward {
  id: number
  name: string
  hasQrcode: boolean
}

interface Contest {
  id: number
  name: string
  slug: string
  fromDate: string
  toDate: string
  stores: StoreWithShortLink[]
  shortLink: ShortLink
  contestGameRewards: ContestGameReward[]
  countWonRewards: number
  countRetrievedRewards: number
  isPaused: boolean
  isLegacy?: boolean
}

interface CustomerMerchant {
  id: number
  phone: string
  firstname: string
  gender: string
  hasOptin: boolean
  country: {
    flag: string
  }
}

interface Participant {
  id: number
  customerMerchant: CustomerMerchant
  participatedAt: string
  retrievedAt: string | null
  contestGameReward: ContestGameReward | null
  store: {
    name: string
  }
}

interface TableColumn {
  id?: string
  label: string
  type?: string
}

interface TableColumns {
  [key: string]: TableColumn
}

interface TablePagination {
  currentPage: number
  lastPage?: number
  total?: number
}

interface Table {
  pagination: TablePagination
  columns: TableColumns
  rows?: any[]
  sort?: any
}

interface RewardStatistics {
  [key: string]: any
}

interface ModalConfig {
  isOpen: boolean
  title: string
  message: string
  confirm: string
  cancel: string
  icon: any
  iconColor?: string
}

const { downloadQrCode } = useDOM()
const { duplicateContestItem } = useSessionStorage()
const { formatNumber } = useNumber()
const { permissions, stores } = useLocalStorage()
const { selectedStore, getStoreById } = useStore()
const { t } = useI18n()
const notification = useNotification()
const route = useRoute()
const router = useRouter()

const loaded = ref(false)
const isModalOpen = ref(false)
const isContestTipsModalOpen = ref(false)
const contest = ref<Contest>({} as Contest)
const participants = ref<Participant[]>([])
const rewards = ref<RewardStatistics>({})
const clickTotalCount = ref('')
const qrcodeDownloadable = ref<HTMLElement | null>(null)
const isProcessingDelete = ref(false)

const table = ref<Table>({
  pagination: {
    currentPage: 1
  },
  columns: {
    customer: {
      id: 'phone',
      label: 'pages.contests.contestDetailPage.tables.thead.customer'
    },
    participatedAt: {
      id: 'lastValidCheckinDate',
      label: 'pages.contests.contestDetailPage.tables.thead.participatedAt'
    },
    reward: {
      label: 'pages.contests.contestDetailPage.tables.thead.reward'
    },
    retrieved: {
      id: 'retrievedAt',
      label: 'pages.contests.contestDetailPage.tables.thead.retrieved'
    },
    store: {
      label: 'pages.contests.contestDetailPage.tables.thead.store'
    }
  }
})

const translatedColumns = computed(() =>
  Object.fromEntries(
    Object.entries(table.value.columns).map(([key, col]) => [key, { ...col, label: t((col as any).label) }])
  )
)

const actionsTitle = t('pages.contests.contestDetailPage.buttons.title')

const duplicateModal = ref<ModalConfig>({
  isOpen: false,
  title: t('pages.contests.contestDetailPage.modal.duplicate.title'),
  message: t('pages.contests.contestDetailPage.modal.duplicate.message'),
  confirm: t('pages.contests.contestDetailPage.modal.duplicate.confirm'),
  cancel: t('pages.contests.contestDetailPage.modal.duplicate.cancel'),
  icon: 'HeroSquare2StackIconSolid'
})

const deleteModal = ref<ModalConfig>({
  isOpen: false,
  title: t('pages.contests.contestDetailPage.modal.delete.title'),
  message: t('pages.contests.contestDetailPage.modal.delete.message'),
  confirm: t('pages.contests.contestDetailPage.modal.delete.confirm'),
  cancel: t('pages.contests.contestDetailPage.modal.delete.cancel'),
  icon: 'HeroTrashIconSolid',
  iconColor: 'red'
})

const actionsValues = computed(() => {
  const canCreate = permissions.value['contest-game.create']
  const canEdit = permissions.value['contest-game.update']
  const canDelete = permissions.value['contest-game.delete']
  const actions = []

  if (canEdit) {
    actions.push({
      id: 1,
      text: t('pages.contests.contestDetailPage.buttons.edit'),
      icon: Pencil
    })
  }

  if (canCreate) {
    actions.push({
      id: 2,
      text: t('pages.contests.contestDetailPage.buttons.duplicate'),
      icon: Copy
    })
  }

  actions.push({
    id: 4,
    text: contest.value.isPaused
      ? t('pages.contests.contestDetailPage.buttons.unpause')
      : t('pages.contests.contestDetailPage.buttons.pause'),
    icon: contest.value.isPaused ? Play : Pause
  })

  if (canDelete) {
    actions.push({
      id: 3,
      text: t('pages.contests.contestDetailPage.buttons.delete'),
      icon: Trash2,
      variant: 'destructive'
    })
  }
  return actions
})

const qrcodeLink = computed(() => {
  let shortLink = shortLinks.value?.shortLink
  if (shortLink && !/^https?:\/\//i.test(shortLink)) {
    shortLink = `https://${shortLink}`
  }
  return shortLink
})

const shortLinks = computed(() => {
  if (!contest.value.stores || contest.value.stores.length === 0) return null

  if (contest.value.stores.length === 1) {
    return contest.value.stores[0]?.shortLink
  }

  return selectedStore.value === 0
    ? contest.value.shortLink
    : contest.value.stores.filter((store) => store.storeId === selectedStore.value)[0]?.shortLink
})

const generateDropdownStores = computed(() => {
  if (!contest.value.stores) return []

  return stores.value.filter((availableStore: Store) =>
    contest.value.stores.map((store) => store.storeId).includes(availableStore.id)
  )
})

const hasMultipleStores = computed(() => {
  return contest.value?.stores?.length > 1
})

const hasQrcode = computed(() => {
  if (!contest.value.contestGameRewards) return false
  return contest.value.contestGameRewards.filter((row) => row.hasQrcode).length > 0
})

const showContestExportButton = () => {
  return permissions.value['contest-game.export']
}

const handleStoreSelected = async () => {
  loaded.value = false
  await fetchData()
  loaded.value = true
}

const selectActions = async (event: number) => {
  if (event === 1) {
    router.push({
      name: 'LegacyContestEdit',
      params: { contestId: route.params.contestId }
    })
    return
  }
  if (event === 2) {
    duplicateModal.value.isOpen = true
    return
  }
  if (event === 3) {
    deleteModal.value.isOpen = true
    return
  }
  if (event === 4) {
    await pauseContest()
    return
  }
}

const pauseContest = async () => {
  await axios.patch(`/contest-games/${route.params.contestId}/is-paused`, {
    isPaused: !contest.value.isPaused
  })

  notification.create('success', {
    text: contest.value.isPaused
      ? t('pages.contests.contestDetailPage.unpauseSuccess')
      : t('pages.contests.contestDetailPage.pauseSuccess')
  })

  await fetchContest()
}

const duplicateContest = () => {
  const params = { ...contest.value }
  params.name = `${params.name} ${t('pages.contests.contestDetailPage.copy')}`
  params.slug = `${params.slug}-${t('pages.contests.contestDetailPage.copy').toLowerCase()}`

  duplicateContestItem.value = params

  router.push({
    name: 'ContestCreate'
  })
}

const deleteContest = () => {
  if (isProcessingDelete.value) {
    return
  }
  isProcessingDelete.value = true
  axios
    .delete(`/contest-games/${route.params.contestId}`)
    .then(() => {
      notification.create('success', {
        text: t('pages.contests.contestDetailPage.modal.notifications.delete.success')
      })
      router.push({
        name: 'ContestList'
      })
    })
    .finally(() => {
      isProcessingDelete.value = false
    })
}

const changePage = (page: number) => {
  table.value.pagination.currentPage = page
  fetchContestParticipants()
}

const fetchContest = async () => {
  try {
    const response = await axios.get(`/contest-games/${route.params.contestId}`, {
      params: {
        storeIds: selectedStore.value
      }
    })

    contest.value = response?.data?.data

    if (contest.value.stores && contest.value.stores.length > 0) {
      const reducer = (accumulator: number, curr: number) => accumulator + curr
      const result = contest.value.stores.map((store) => store.shortLink.clicksCount)
      clickTotalCount.value = result.reduce(reducer).toString()
    }
  } catch (error: any) {
    if (error.response && error.response.status === 404) {
      router.push({ name: 'ContestList' })
    }
    if (error.response && error.response.status === 403) {
      notification.create('error', {
        text: t('pages.contests.contestDetailPage.notifications.unauthorized')
      })
      router.push({ name: 'ContestList' })
    }
  }
}

const fetchContestReward = async () => {
  const response = await axios.get(`/contest-games/${route.params.contestId}/reward-statistics`, {
    params: {
      storeIds: selectedStore.value
    }
  })
  rewards.value = response.data.data
}

const fetchContestParticipants = async () => {
  const response = await axios.get(`contest-games/${route.params.contestId}/participants`, {
    params: {
      page: table.value.pagination.currentPage,
      storeIds: selectedStore.value
    }
  })

  participants.value = response.data.data
  const rows = []

  participants.value.forEach((row: Participant) => {
    let retrieved = null
    if (row.contestGameReward?.hasQrcode) {
      retrieved =
        row.retrievedAt !== null
          ? {
              icon: 'HeroCheckCircleIconSolid',
              color: 'green',
              colorIntensity: 500
            }
          : {
              icon: 'HeroXCircleIconSolid',
              color: 'gray',
              colorIntensity: 500
            }
    }

    rows.push({
      id: row.id,
      data: {
        customer: {
          id: row.customerMerchant.id,
          phone: row.customerMerchant.phone,
          name: row.customerMerchant.firstname,
          gender: row.customerMerchant.gender,
          hasOptin: row.customerMerchant.hasOptin,
          countryFlag: row.customerMerchant.country.flag
        },
        participatedAt: {
          date: new Date(row.participatedAt).toLocaleDateString('fr-FR'),
          time: new Date(row.participatedAt).toLocaleTimeString('fr-FR')
        },
        reward: row.contestGameReward !== null ? row.contestGameReward.name : '-',
        retrieved: retrieved,
        store: row.store.name
      }
    })
  })

  table.value.rows = rows
  table.value.pagination = {
    currentPage: response.data.meta.current_page,
    lastPage: response.data.meta.last_page,
    total: response.data.meta.total
  }
}

const fetchData = async () => {
  await Promise.all([fetchContest(), fetchContestParticipants(), fetchContestReward()])
}

const openContestTipsModal = () => {
  const hasRouteParams = route.params?.tips && route.params.tips.length > 0
  if (hasRouteParams) {
    isContestTipsModalOpen.value = true
  }
}

const displayToDate = () => {
  return dayjs(contest.value.toDate).format('DD/MM/YYYY') !== '01/01/3000'
}

const formatDate = (date: string) => {
  return dayjs(date).format('D MMMM YYYY')
}

const downloadQr = (element: HTMLElement | null) => {
  if (!element) return

  const isSingleStore = selectedStore.value !== 0
  const name = `${contest.value.slug}${isSingleStore ? `-${getStoreById(selectedStore.value).slug}` : ''}`
  const filename = `pongo-contest-${name}.png`
  downloadQrCode(element, filename)
}

onMounted(async () => {
  selectedStore.value = 0
  loaded.value = false
  await fetchData()
  loaded.value = true
  openContestTipsModal()
})
</script>

<template>
  <div>
    <TheDetailHeader
      v-if="contest?.name"
      :title="contest.name"
      :parent="{ label: t('pages.contests.contestsPage.title'), to: { name: 'ContestList' } }"
    >
      <template #badge>
        <ContestStatus v-if="contest" :contest="contest" />
      </template>
      <template #actions>
        <DropdownMenu>
          <DropdownMenuTrigger as-child>
            <Button variant="outline" class="w-full md:w-[190px]">
              {{ actionsTitle }}
              <ChevronDown class="ml-auto" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" class="w-[190px]">
            <DropdownMenuItem
              v-for="action in actionsValues"
              :key="action.id"
              :variant="action.variant"
              @click="selectActions(action.id)"
            >
              <component :is="action.icon" />
              {{ action.text }}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <BaseModal
          :is-open="duplicateModal.isOpen"
          :icon="duplicateModal.icon"
          :link-text="duplicateModal.confirm"
          :text-title="duplicateModal.title"
          :cancel="duplicateModal.cancel"
          :text="duplicateModal.message"
          @close="duplicateModal.isOpen = false"
          @click="duplicateContest"
        />
        <BaseModal
          :is-open="deleteModal.isOpen"
          :icon="deleteModal.icon"
          :icon-color="deleteModal.iconColor"
          :link-text="deleteModal.confirm"
          :text-title="deleteModal.title"
          :cancel="deleteModal.cancel"
          :text="deleteModal.message"
          @close="deleteModal.isOpen = false"
          @click="deleteContest"
        />
      </template>
    </TheDetailHeader>

    <div v-if="hasMultipleStores" class="mb-6 px-6 sm:px-12">
      <ThePageSettings
        :stores-list="generateDropdownStores"
        :display-store-selector="true"
        @store-selected="handleStoreSelected"
      />
    </div>

    <div v-if="loaded" class="w-full">
      <div class="mt-5 px-6 pb-6 sm:mt-0 sm:px-12 sm:pb-12">
        <div class="mt-5 flex flex-wrap items-center justify-start gap-2 sm:mt-11 lg:mt-0"></div>
        <div
          class="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2"
          :class="hasQrcode ? 'lg:grid-cols-4' : 'lg:grid-cols-3'"
        >
          <AppStatsCard
            :title="t('pages.contests.contestDetailPage.cards.clicks')"
            :content="selectedStore == 0 ? formatNumber(clickTotalCount) : formatNumber(shortLinks?.clicksCount)"
          />
          <AppStatsCard
            :title="t('pages.contests.contestDetailPage.cards.participation')"
            :content="formatNumber(table.pagination.total)"
          />
          <AppStatsCard
            class="text-left"
            :clickable="true"
            :title="t('pages.contests.contestDetailPage.cards.rewardsWon')"
            @click="isModalOpen = !isModalOpen"
          >
            <div class="flex w-full items-end justify-between">
              <p>{{ formatNumber(contest.countWonRewards) }}</p>
              <p class="text-xs font-medium underline">
                {{ t('pages.contests.contestDetailPage.blocks.details.detail') }}
              </p>
            </div>
          </AppStatsCard>
          <AppStatsCard
            v-if="hasQrcode"
            :clickable="true"
            :title="t('pages.contests.contestDetailPage.cards.rewardsRetrieved')"
            @click="isModalOpen = !isModalOpen"
          >
            <div class="flex w-full items-end justify-between">
              <p>{{ contest.countRetrievedRewards }}</p>
              <p class="text-xs font-medium underline">
                {{ t('pages.contests.contestDetailPage.blocks.details.detail') }}
              </p>
            </div>
          </AppStatsCard>
          <BaseModal
            :is-open="isModalOpen"
            :heading-title="t('pages.contests.contestDetailPage.modal.title')"
            size="3xl"
            :link-text="t('global.words.close')"
            @close="isModalOpen = false"
            @click="isModalOpen = false"
          >
            <ContestsModal :rewards="rewards" :has-qrcode="hasQrcode" />
          </BaseModal>
        </div>
      </div>

      <div class="flex flex-col-reverse px-6 pb-6 sm:px-12 sm:pb-12 2xl:grid 2xl:grid-cols-3 2xl:flex-col">
        <div class="col-span-2">
          <div class="2xl:col-span-2">
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-semibold leading-6 text-foreground">
                {{ t('pages.contests.contestDetailPage.blocks.customerConverted.title') }}
              </h3>
              <AppDownloadCsvButton
                v-if="showContestExportButton()"
                :path="`contest-games/${route.params.contestId}/downloadCsv`"
                :is-batch="true"
              />
            </div>
            <div v-if="table.rows && table.rows.length > 0" class="mt-5 overflow-x-auto">
              <AppTable
                :columns="translatedColumns"
                :rows="table.rows"
                :pagination="table.pagination"
                :sort="table.sort"
                @update:change-page="changePage"
              >
                <template #td-customer="{ data }">
                  <RowCustomer
                    :id="data.customer.id"
                    :country-flag="data.customer.countryFlag ?? null"
                    :has-optin="data.customer.hasOptin"
                    :name="data.customer.name"
                    :phone="data.customer.phone"
                  />
                </template>
                <template #td-participatedAt="{ data }">
                  <div>
                    <div class="text-sm font-medium text-foreground">{{ data.participatedAt.date }}</div>
                    <div v-if="data.participatedAt.time" class="text-sm text-muted-foreground">
                      {{ data.participatedAt.time }}
                    </div>
                  </div>
                </template>
                <template #td-retrieved="{ data }">
                  <BaseIcon
                    :name="data.retrieved.icon"
                    :class="`h-5 w-5 text-${data.retrieved.color}-${data.retrieved.colorIntensity ?? 500}`"
                  />
                </template>
              </AppTable>
            </div>
            <div v-else class="mt-5 rounded-md border bg-white p-12">
              <div class="flex items-center justify-center">
                <div class="text-center">
                  <img
                    src="/images/avatars/woman01.jpg"
                    class="m-6 inline-block h-28 w-28 rounded-full border-8 border-primary/15"
                  />
                  <h3 class="text-lg font-semibold text-foreground">
                    {{ t('pages.contests.contestDetailPage.blocks.customerConverted.noCustomer.title') }}
                  </h3>
                  <p class="text-sm text-foreground">
                    {{ t('pages.contests.contestDetailPage.blocks.customerConverted.noCustomer.description') }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="2xl:col-span-1 2xl:ml-4">
          <h3 class="mt-5 text-lg font-semibold leading-6 text-foreground sm:mt-0">
            {{ t('pages.contests.contestDetailPage.blocks.yourContest') }}
          </h3>

          <div class="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:block">
            <div class="col-span-1 mt-4 rounded-md border bg-white">
              <div class="border-b px-4 py-5 sm:px-6">
                <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
                  <div class="ml-2 mt-2">
                    <h3 class="text-lg font-medium leading-6 text-foreground">
                      {{ t('pages.contests.contestDetailPage.blocks.details.title') }}
                    </h3>
                  </div>
                </div>
              </div>
              <div class="px-4 py-5 sm:px-6">
                <dl class="grid grid-cols-2 gap-x-2 gap-y-8 text-xs sm:text-sm">
                  <div class="sm:col-span-1">
                    <dt class="font-medium text-muted-foreground">
                      {{ t('pages.contests.contestDetailPage.information.fromDate') }}
                    </dt>
                    <dd class="mt-1 text-foreground">
                      {{ formatDate(contest.fromDate) }}
                    </dd>
                  </div>
                  <div class="sm:col-span-1">
                    <dt class="font-medium text-muted-foreground">
                      {{ t('pages.contests.contestDetailPage.information.toDate') }}
                    </dt>
                    <dd class="mt-1 text-foreground">
                      <span v-if="displayToDate()">
                        {{ formatDate(contest.toDate) }}
                      </span>
                      <span v-else> - </span>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            <div class="col-span-1 mt-4 rounded-md border bg-white">
              <div class="border-b px-4 py-5 sm:px-6">
                <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
                  <div class="ml-2 mt-2">
                    <h3 class="text-lg font-medium leading-6 text-foreground">
                      {{ t('pages.contests.contestDetailPage.blocks.links.title') }}
                    </h3>
                  </div>
                </div>
              </div>
              <div class="px-4 py-5 sm:px-6">
                <dl class="grid grid-cols-3 gap-x-2 gap-y-8 text-xs sm:text-sm">
                  <div class="sm:col-span-1">
                    <dt class="font-medium text-muted-foreground">
                      {{ t('pages.contests.contestDetailPage.blocks.links.qrcode') }}
                    </dt>
                    <dd class="mt-1 text-foreground">
                      <qrcode-vue :value="qrcodeLink" :size="96" level="H" />
                      <div ref="qrcodeDownloadable" class="hidden">
                        <qrcode-vue :value="qrcodeLink" :size="1000" level="H" />
                      </div>
                    </dd>
                    <dt class="font-medium text-muted-foreground">
                      <button
                        class="mt-1 flex text-left text-xs text-muted-foreground hover:text-muted-foreground"
                        @click="downloadQr(qrcodeDownloadable)"
                      >
                        <BaseIcon name="HeroArrowDownTrayIconSolid" class="h-4 w-4" />
                        {{ t('global.words.download') }}
                      </button>
                    </dt>
                  </div>
                  <div v-if="shortLinks?.shortLink" class="col-span-3 flex flex-col lg:col-span-2">
                    <div>
                      <dt class="font-medium text-muted-foreground">
                        {{ t('pages.contests.contestDetailPage.blocks.links.link') }}
                      </dt>
                      <dd class="mt-1 flex break-all text-foreground">
                        <CopyLink :link="shortLinks.shortLink" text="global.words.copy" />
                      </dd>
                    </div>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <TheLoadingPage v-else class="w-full" />
  </div>
</template>
